import { zipSync, strToU8 } from "fflate";
import { buildBounds } from "../decode/sweeps.js";

const PNG_HREF = "files/radar.png";

export function kmlWhen(dateClean, timeStr) {
  const d = String(dateClean || "").replace(/-/g, "");
  const t = String(timeStr || "").replace(/:/g, "");
  if (d.length < 8 || t.length < 6) return "";
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}T${t.slice(0, 2)}:${t.slice(2, 4)}:${t.slice(4, 6)}Z`;
}

export function kmzFilename(polar, { lastPolar } = {}) {
  const station = String(polar?.station || "radar").replace(/[^A-Za-z0-9]/g, "");
  const date = String(polar?.date || "").replace(/-/g, "");
  const time = String(polar?.time || "").replace(/:/g, "");
  const product = String(polar?.product || "scan").replace(/[^A-Za-z0-9_]+/g, "_");
  const lastTime = String(lastPolar?.time || "").replace(/:/g, "");
  if (lastPolar && lastTime && lastTime !== time) {
    return `${station}_${date}_${time}-${lastTime}_${product}.kmz`;
  }
  return `${station}_${date}_${time}_${product}.kmz`;
}

export function overlayBounds(polar) {
  if (polar?.bounds && Number.isFinite(polar.bounds.north)) return polar.bounds;
  const rangeKm = Number(polar?.max_range_km) || 230;
  return buildBounds(polar.lat, polar.lon, rangeKm);
}

export function imageSizeForBounds(bounds, maxSize = 1536) {
  const latSpan = Math.max(1e-6, bounds.north - bounds.south);
  const lonSpan = Math.max(1e-6, bounds.east - bounds.west);
  const aspect = lonSpan / latSpan;
  if (aspect >= 1) {
    return { width: maxSize, height: Math.max(32, Math.round(maxSize / aspect)) };
  }
  return { width: Math.max(32, Math.round(maxSize * aspect)), height: maxSize };
}

function escapeXml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function stormLatLon(pt) {
  if (!pt) return null;
  if (Array.isArray(pt) && pt.length >= 2 && Number.isFinite(pt[0]) && Number.isFinite(pt[1])) {
    return { lat: pt[0], lon: pt[1] };
  }
  if (Number.isFinite(pt.lat) && Number.isFinite(pt.lon)) return { lat: pt.lat, lon: pt.lon };
  return null;
}

function coord(pt) {
  const ll = stormLatLon(pt);
  return ll ? `${ll.lon},${ll.lat},0` : "";
}

export function rasterizePolarRgba(polar, decoded, paletteRgba, { maxSize = 1536 } = {}) {
  const bounds = overlayBounds(polar);
  const { width, height } = imageSizeForBounds(bounds, maxSize);
  const pixels = new Uint8ClampedArray(width * height * 4);
  const values = decoded.values;
  const numGates = decoded.numGates || polar.num_gates;
  const numAz = decoded.numAz || polar.num_azimuths;
  const azStart = decoded.azimuthStartDeg || polar.azimuths?.[0] || 0;
  const rangeStart = Number(polar.range_start_m) || 0;
  const rangeStep = Number(polar.range_step_m) || 250;
  const maxRangeM = rangeStart + numGates * rangeStep;
  const mPerDegLat = 111000;
  const mPerDegLon = 111000 * Math.cos((polar.lat * Math.PI) / 180);
  const lonSpan = bounds.east - bounds.west;
  const latSpan = bounds.north - bounds.south;
  const palette = paletteRgba || new Uint8Array(4);

  for (let y = 0; y < height; y++) {
    const lat = bounds.north - ((y + 0.5) / height) * latSpan;
    const northM = (lat - polar.lat) * mPerDegLat;
    for (let x = 0; x < width; x++) {
      const lon = bounds.west + ((x + 0.5) / width) * lonSpan;
      const eastM = (lon - polar.lon) * mPerDegLon;
      const rangeM = Math.hypot(eastM, northM);
      if (rangeM > maxRangeM) continue;
      const gateF = (rangeM - rangeStart) / rangeStep;
      if (gateF < 0 || gateF >= numGates) continue;
      let azDeg = Math.atan2(eastM, northM) * (180 / Math.PI);
      if (azDeg < 0) azDeg += 360;
      const azNorm = (azDeg - azStart + 360) % 360;
      const azIdx = Math.min(numAz - 1, Math.max(0, Math.floor((azNorm / 360) * numAz)));
      const gateIdx = Math.min(numGates - 1, Math.max(0, Math.floor(gateF)));
      const idx = values[azIdx * numGates + gateIdx] || 0;
      if (idx < 1) continue;
      const po = idx * 4;
      const out = (y * width + x) * 4;
      pixels[out] = palette[po] || 0;
      pixels[out + 1] = palette[po + 1] || 0;
      pixels[out + 2] = palette[po + 2] || 0;
      const alpha = palette[po + 3] || 0;
      pixels[out + 3] = alpha > 0 ? Math.max(alpha, 150) : 0;
    }
  }
  return { width, height, pixels, bounds };
}

export function isoFromMs(ms) {
  return new Date(ms).toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function timeSpansForWhens(whens, fallbackMs = 5 * 60 * 1000) {
  let lastMs = 0;
  const begins = (whens || []).map((when) => {
    let ms = Date.parse(when);
    if (!Number.isFinite(ms)) ms = lastMs + 1000;
    if (ms <= lastMs) ms = lastMs + 1000;
    lastMs = ms;
    return ms;
  });
  return begins.map((beginMs, i) => {
    const next = begins[i + 1];
    const endMs = Number.isFinite(next) ? next : beginMs + fallbackMs;
    return { begin: isoFromMs(beginMs), end: isoFromMs(endMs) };
  });
}

function lineString(points) {
  const coords = points.map(coord).filter(Boolean).join(" ");
  if (!coords) return "";
  return `<LineString><tessellate>1</tessellate><coordinates>${coords}</coordinates></LineString>`;
}

function overlayTimeXml(overlay) {
  if (overlay.begin && overlay.end) {
    return `<TimeSpan><begin>${escapeXml(overlay.begin)}</begin><end>${escapeXml(overlay.end)}</end></TimeSpan>`;
  }
  if (overlay.when) {
    return `<TimeStamp><when>${escapeXml(overlay.when)}</when></TimeStamp>`;
  }
  return "";
}

function groundOverlayXml(overlay) {
  const box = overlay.bounds;
  return `
    <GroundOverlay>
      <name>${escapeXml(overlay.name)}</name>
      ${overlayTimeXml(overlay)}
      <color>ffffffff</color>
      <Icon><href>${escapeXml(overlay.href || PNG_HREF)}</href></Icon>
      <LatLonBox>
        <north>${box.north}</north>
        <south>${box.south}</south>
        <east>${box.east}</east>
        <west>${box.west}</west>
      </LatLonBox>
    </GroundOverlay>`;
}

function kmlPolyColor(hex, alphaHex = "44") {
  const raw = String(hex || "#ffffff").replace("#", "");
  const rr = raw.slice(0, 2);
  const gg = raw.slice(2, 4);
  const bb = raw.slice(4, 6);
  return `${alphaHex}${bb}${gg}${rr}`;
}

function ringCoords(ring) {
  return (ring || [])
    .map((pos) => {
      const lon = Number(pos?.[0]);
      const lat = Number(pos?.[1]);
      return Number.isFinite(lat) && Number.isFinite(lon) ? `${lon},${lat},0` : "";
    })
    .filter(Boolean)
    .join(" ");
}

function polygonXml(rings) {
  if (!rings?.length) return "";
  const outer = ringCoords(rings[0]);
  if (!outer) return "";
  const holes = (rings.slice(1) || []).map((ring) => {
    const coords = ringCoords(ring);
    return coords
      ? `<innerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></innerBoundaryIs>`
      : "";
  }).join("");
  return `<Polygon><outerBoundaryIs><LinearRing><coordinates>${outer}</coordinates></LinearRing></outerBoundaryIs>${holes}</Polygon>`;
}

function geometryXml(geometry) {
  if (!geometry) return "";
  if (geometry.type === "Polygon") return polygonXml(geometry.coordinates);
  if (geometry.type === "MultiPolygon") {
    const parts = (geometry.coordinates || []).map(polygonXml).filter(Boolean).join("");
    return parts ? `<MultiGeometry>${parts}</MultiGeometry>` : "";
  }
  return "";
}

function alertsXml(alerts) {
  const features = alerts || [];
  if (!features.length) return "";
  const marks = features.map((alert) => {
    const geom = geometryXml(alert.geometry);
    if (!geom) return "";
    const when = alert.issue ? `<TimeSpan><begin>${escapeXml(alert.issue)}</begin>${alert.expire ? `<end>${escapeXml(alert.expire)}</end>` : ""}</TimeSpan>` : "";
    const color = kmlPolyColor(alert.color || "#ffaa00", "55");
    const line = kmlPolyColor(alert.color || "#ffaa00", "cc");
    return `
      <Placemark>
        <name>${escapeXml(alert.label || alert.title || "Alert")}</name>
        ${when}
        <Style>
          <LineStyle><color>${line}</color><width>2</width></LineStyle>
          <PolyStyle><color>${color}</color></PolyStyle>
        </Style>
        ${geom}
      </Placemark>`;
  }).join("");
  return `
    <Folder>
      <name>Watches, warnings &amp; MDs</name>
      ${marks}
    </Folder>`;
}

function lightningXml(flashes) {
  const points = (flashes || []).slice(0, 400);
  if (!points.length) return "";
  const marks = points.map((flash, index) => {
    const when = Number.isFinite(flash.timeMs) ? new Date(flash.timeMs).toISOString() : "";
    return `
      <Placemark>
        <name>GLM ${index + 1}</name>
        ${when ? `<TimeStamp><when>${escapeXml(when)}</when></TimeStamp>` : ""}
        <Style><IconStyle><color>ff00ffff</color><scale>0.55</scale></IconStyle></Style>
        <Point><coordinates>${flash.lon},${flash.lat},0</coordinates></Point>
      </Placemark>`;
  }).join("");
  return `
    <Folder>
      <name>GLM lightning</name>
      ${marks}
    </Folder>`;
}

export function buildKml({
  name,
  description,
  bounds,
  station,
  storms = [],
  flashes = [],
  alerts = [],
  when = "",
  begin = "",
  end = "",
  pngHref = PNG_HREF,
  overlays = null,
} = {}) {
  const overlayList = overlays?.length
    ? overlays
    : [{ name, when, begin, end, href: pngHref, bounds }];
  const docTime = begin && end
    ? `<TimeSpan><begin>${escapeXml(begin)}</begin><end>${escapeXml(end)}</end></TimeSpan>`
    : "";
  const stormXml = (storms || []).map((storm) => {
    const here = { lat: storm.lat, lon: storm.lon };
    const past = [...(storm.past || [])].reverse().concat([here]);
    const forecast = [here].concat(storm.forecast || []);
    const label = storm.tvs ? `Storm ${storm.id} (TVS)` : `Storm ${storm.id}`;
    return `
      <Placemark>
        <name>${escapeXml(label)}</name>
        <Style><IconStyle><color>ff0088ff</color><scale>0.9</scale></IconStyle></Style>
        <Point><coordinates>${coord(here)}</coordinates></Point>
      </Placemark>
      <Placemark>
        <name>${escapeXml(`Storm ${storm.id} past`)}</name>
        <Style><LineStyle><color>7f44aaff</color><width>2</width></LineStyle></Style>
        ${lineString(past)}
      </Placemark>
      <Placemark>
        <name>${escapeXml(`Storm ${storm.id} forecast`)}</name>
        <Style><LineStyle><color>d0ffffff</color><width>2.5</width></LineStyle></Style>
        ${lineString(forecast)}
      </Placemark>`;
  }).join("");

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(name)}</name>
    <description>${escapeXml(description)}</description>
    ${docTime}
    <LookAt>
      <longitude>${station.lon}</longitude>
      <latitude>${station.lat}</latitude>
      <range>380000</range>
      <tilt>0</tilt>
      <heading>0</heading>
    </LookAt>
    <Folder>
      <name>Radar frames</name>
      ${overlayList.map(groundOverlayXml).join("")}
    </Folder>
    <Placemark>
      <name>${escapeXml(station.id)}</name>
      <description>${escapeXml(station.name || station.id)}</description>
      <Point><coordinates>${station.lon},${station.lat},0</coordinates></Point>
    </Placemark>
    ${stormXml}
    ${lightningXml(flashes)}
    ${alertsXml(alerts)}
  </Document>
</kml>
`;
}

export function packKmz(kml, pngOrFiles) {
  const files = pngOrFiles instanceof Uint8Array
    ? { [PNG_HREF]: pngOrFiles }
    : pngOrFiles;
  const zip = { "doc.kml": strToU8(kml) };
  for (const [name, bytes] of Object.entries(files || {})) {
    zip[name] = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  }
  return zipSync(zip, { level: 6 });
}

function pixelsToPngBytes({ width, height, pixels }, canvas) {
  if (typeof document === "undefined") {
    throw new Error("PNG encode needs a browser canvas");
  }
  const target = canvas || document.createElement("canvas");
  target.width = width;
  target.height = height;
  const ctx = target.getContext("2d");
  ctx.putImageData(new ImageData(pixels, width, height), 0, 0);
  return new Promise((resolve, reject) => {
    target.toBlob((blob) => {
      if (!blob) {
        reject(new Error("PNG encode failed"));
        return;
      }
      blob.arrayBuffer().then((buf) => resolve(new Uint8Array(buf)), reject);
    }, "image/png");
  });
}

export function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export async function buildRadarKmz({
  frames,
  polar,
  decoded,
  paletteRgba,
  station,
  storms = [],
  flashes = [],
  alerts = [],
  shareUrl = "",
  productLabel = "",
  onProgress,
} = {}) {
  const list = (frames && frames.length)
    ? frames
    : [{ polar, decoded }];
  const maxSize = list.length > 1 ? 1280 : 1536;
  const canvas = typeof document !== "undefined" ? document.createElement("canvas") : null;
  const files = {};
  const overlays = [];
  const whens = list.map((item) => kmlWhen(item.polar.date, item.polar.time));
  const spans = timeSpansForWhens(whens);
  const label = productLabel || list[0].polar.product || "Radar";

  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    onProgress?.({ index: i + 1, total: list.length, text: `Exporting frame ${i + 1} / ${list.length}…` });
    const raster = rasterizePolarRgba(item.polar, item.decoded, paletteRgba, { maxSize });
    const href = list.length === 1 ? PNG_HREF : `files/radar_${String(i).padStart(3, "0")}.png`;
    files[href] = await pixelsToPngBytes(raster, canvas);
    overlays.push({
      name: `${item.polar.time || `Frame ${i + 1}`} UTC`,
      begin: spans[i].begin,
      end: spans[i].end,
      href,
      bounds: raster.bounds,
    });
    if (i + 1 < list.length) await new Promise((resolve) => setTimeout(resolve, 0));
  }

  const first = list[0].polar;
  const last = list[list.length - 1].polar;
  const name = list.length > 1
    ? `${first.station || station.id} ${label} (${list.length} frames)`
    : `${first.station || station.id} ${spans[0].begin} ${label}`.trim();
  const description = [
    `${station.id} ${label}`,
    list.length > 1 ? `${spans[0].begin} – ${spans[spans.length - 1].end} UTC` : (whens[0] ? `UTC ${whens[0]}` : ""),
    shareUrl,
  ].filter(Boolean).join("\n");
  const kml = buildKml({
    name,
    description,
    bounds: overlays[0].bounds,
    station: { id: station.id, name: station.name || station.id, lat: first.lat, lon: first.lon },
    storms,
    flashes,
    alerts,
    begin: spans[0].begin,
    end: spans[spans.length - 1].end,
    overlays,
  });
  const bytes = packKmz(kml, files);
  return {
    filename: kmzFilename(first, { lastPolar: last }),
    blob: new Blob([bytes], { type: "application/vnd.google-earth.kmz" }),
  };
}
